export interface NotificationCopyFields {
  event: string;
  impact: string;
  nextStep: string;
}

export interface NotificationCopyInput {
  event?: string | null;
  impact?: string | null;
  nextStep?: string | null;
}

export interface NotificationFormatOptions {
  timestamp?: string;
  referenceId?: string;
  locale?: string;
}

export interface NotificationFormattedCopy {
  title: string;
  body: string;
  meta?: string;
}

const DEFAULT_EVENT = "Notification update";
const DEFAULT_IMPACT = "Review the notification details.";
const DEFAULT_NEXT_STEP = "Review details to resolve.";

const trimToUndefined = (value?: string | null): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

const formatTimestamp = (timestamp: string, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch {
    return timestamp;
  }
};

export const normalizeNotificationCopy = (
  input: NotificationCopyInput,
): NotificationCopyFields => {
  const event = trimToUndefined(input.event) ?? DEFAULT_EVENT;
  const impact = trimToUndefined(input.impact) ?? DEFAULT_IMPACT;
  const nextStep = trimToUndefined(input.nextStep) ?? DEFAULT_NEXT_STEP;

  return { event, impact, nextStep };
};

export const validateNotificationCopy = (copy: NotificationCopyFields): void => {
  if (!trimToUndefined(copy.event)) {
    throw new Error("Notification copy requires a non-empty event.");
  }
  if (!trimToUndefined(copy.impact)) {
    throw new Error("Notification copy requires a non-empty impact.");
  }
  if (!trimToUndefined(copy.nextStep)) {
    throw new Error("Notification copy requires a non-empty next step.");
  }
};

export const formatNotificationCopy = (
  copy: NotificationCopyFields,
  options: NotificationFormatOptions = {},
): NotificationFormattedCopy => {
  validateNotificationCopy(copy);
  const locale =
    options.locale ||
    (typeof navigator !== "undefined" ? navigator.language : "en-US");

  const bodySegments = [copy.impact.trim()];
  if (copy.nextStep) {
    bodySegments.push(`Next: ${copy.nextStep.trim()}`);
  }

  const metaSegments: string[] = [];
  if (options.referenceId) {
    metaSegments.push(`Ref ${options.referenceId}`);
  }
  if (options.timestamp) {
    metaSegments.push(formatTimestamp(options.timestamp, locale));
  }

  return {
    title: copy.event.trim(),
    body: bodySegments.join(" "),
    meta: metaSegments.length > 0 ? metaSegments.join(" • ") : undefined,
  };
};

export interface UndoToastCopy {
  title: string;
  description: string;
  actionLabel: string;
}

export const buildUndoToastCopy = (
  copy: NotificationCopyFields,
  actionLabel = "Undo",
  options?: NotificationFormatOptions,
): UndoToastCopy => {
  const formatted = formatNotificationCopy(copy, options);
  return {
    title: formatted.title,
    description: formatted.body,
    actionLabel,
  };
};
